
    import Vue from "vue";
    import Component from "vue-class-component";
    import { StateChanger } from "vue-infinite-loading";
    import { Watch } from "vue-property-decorator";
    import Utils from "@/utilities/Utils";
    import ApiClient from "@/utilities/ApiClient";
    import ReportBar from "@/vue/components/ReportBar.vue";
    import DatePicker from "@/vue/components/DatePicker.vue";
    import { QuestionReportParameters } from "@/model/QuestionReportParameters";
    import { IQuestionReportItem, QuestionReportItem } from "@/model/QuestionReportItem";
    import { ISiteLookup } from "@/model/Site";
    import { ILookupItem } from "@/model/LookupItem";
    import { UserRole } from "@/model/Enums";
    import { IClientLookup } from "@/model/Client";

    interface ISearchResponse {
        list: Array<IQuestionReportItem>;
        count: number;
    }

    @Component({
        components: { ReportBar, DatePicker }
    })
    export default class QuestionReport extends Vue {

        async mounted(): Promise<void> {
            await Promise.all([
                (async () => { this.clientList = await ApiClient.get(Utils.addClientQueryParameters("/api/client/lookups")); })(),
                (async () => { this.siteList = await ApiClient.get(Utils.addClientQueryParameters("/api/site/lookups")); })()
            ]);
        }

        infiniteId: number = +new Date();
        searchParameters = new QuestionReportParameters();

        get canFilterClient(): boolean { return this.$store.state.signedInUser?.canFilterClient === true; }
        get canFilterSite(): boolean { return this.$store.state.signedInUser?.canFilterSite === true; }
        get canFilterPathway(): boolean { return this.pathwayList.length > 1; }

        clientList: Array<IClientLookup> = [];
        get clientOptions(): Array<IClientLookup> { return Utils.clientOptionsFilter(this.clientList); }
        siteList: Array<ISiteLookup> = [];
        get siteOptions(): Array<ISiteLookup> { return Utils.siteOptionsFilter(this.siteList, this.searchParameters.clientID); }
        pathwayList: Array<ILookupItem> = [];
        get pathwayOptions(): Array<ILookupItem> { return Utils.optionsFilter(this.pathwayList); }

        resultCount: string = "-";
        list: Array<QuestionReportItem> = [];

        refreshSearch(): void {
            this.resultCount = "-";
            this.list = [];
            this.searchParameters.pageNumber = 1;
            this.infiniteId += 1;
        }

        @Watch("searchParameters.clientID")
        async onClientchanged(): Promise<void> {
            this.pathwayList = [];
            await this.refreshSearch();
        }

        @Watch("searchParameters.siteID")
        async onSitechanged(): Promise<void> {
            await this.loadPathways();
            await this.refreshSearch();
        }

        @Watch("searchParameters.pathwayID")
        async onPathwaychanged(): Promise<void> {
            await this.refreshSearch();
        }

        @Watch("searchParameters.start")
        async onStartchanged(): Promise<void> {
            if(this.datesAreOutofOrder()) {
                this.searchParameters.end = this.searchParameters.start;
            }
            else {
                await this.refreshSearch();
            }
        }

        @Watch("searchParameters.end")
        async onEndchanged(): Promise<void> {
            if(this.datesAreOutofOrder()) {
                this.searchParameters.start = this.searchParameters.end;
            }
            else {
                await this.refreshSearch();
            }
        }

        datesAreOutofOrder(): boolean {
            return Utils.hasDateValue(this.searchParameters.start)
                && Utils.hasDateValue(this.searchParameters.end)
                && this.searchParameters.start > this.searchParameters.end;
        }

        // sites are filtered on client but pathways filtered on server (hmm??)
        async loadPathways(): Promise<void> {
            if(!this.searchParameters.siteID) {
                this.pathwayList = [];
                return;
            }
            this.pathwayList = await ApiClient.get(`/api/pathway/lookups?siteID=${this.searchParameters.siteID}`);
        }

        async infiniteLoadingHandler(stateChanger: StateChanger): Promise<void> {
            const response: ISearchResponse  = await ApiClient.post("/api/report/questions", this.searchParameters);
            if(this.searchParameters.pageNumber === 1) {
                this.resultCount = String(response.count);
            }
            if (response.list.length) {
                this.list.push(...response.list.map((q: IQuestionReportItem) => new QuestionReportItem(q)));
                this.searchParameters.pageNumber += 1;
                stateChanger.loaded();
            }
            else {
                stateChanger.complete();
            }
        }
    }
